import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { config } from "../../../config";
import {
  ConvertToReactSelect,
  getTransformedDataForPagination,
  LocalStorageKeys,
} from "../../../utils";
import { SelectBoxOption } from "../../../interfaces";

interface Permission {
  // Define the structure of Permission if needed
}

interface RoleDefinition {
  description?: string;
  id: string;
  name: string;
  permissions?: Permission[];
}

interface License {
  // Define the structure of License if needed
}

interface UserMetadata {
  description?: string; // Example metadata fields: color_preference, blog_url, etc.
  [key: string]: any;
}

export interface User {
  description?: string;
  email: string;
  name: string;
  nickname: string;
  picture: string; // Should match pattern: ^https?://
  user_id: string; // readOnly
  user_metadata?: UserMetadata;
  created_at: string; // $date-time, readOnly
  last_ip?: string; // readOnly
  last_login?: string; // $date-time, readOnly
  last_password_reset?: string; // $date-time, readOnly, only for database connection users
  license?: License;
  logins_count?: number; // $int64, readOnly
  roles?: RoleDefinition[]; // readOnly
  updated_at?: string; // $date-time, readOnly
  url?: string; // readOnly
}


export const usersAPI = createApi({
  reducerPath: "usersAPI",
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: config.api_url + "um/",
      prepareHeaders: (headers, { getState }) => {
        const token = localStorage.getItem(LocalStorageKeys.authToken);

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }

        return headers;
      },
    }),
    { maxRetries: 2 }
  ),
  tagTypes: ["Users"],
  endpoints: (builder) => ({
    // Get All Users API
    getUsers: builder.query<{ data: User[], totalCount: number }, { search?: string, page?: number, pageSize?: number }>({
      query: ({ search = "", page = 0, pageSize = 100 }) => ({
        url: `user${page !== null ? `?page_no=${page}` : ""}${pageSize ? `&page_size=${pageSize}` : ""
          }`,
        method: "GET",
      }),
      transformResponse: (
        data: User[],
        meta: { request: Request; response?: Response }
      ) => {
        return getTransformedDataForPagination(data, meta?.response);
      },
    }),

    // Get Users as DD Options API
    getUsersAsOptions: builder.query<SelectBoxOption[], null>({
      query: () => ({ url: `user`, method: "GET" }),
      transformResponse: (response: User[], meta, arg) => ConvertToReactSelect(response, "user_id", "name"),
    }),

    // Get User by id API
    getUserById: builder.query<User, string>({
      query: (id) => ({ url: `user/${id}`, method: "GET" }),
    }),

    // Update role for user
    updateRoleForUser: builder.mutation({
      query: ({ user_id, role_id }) => ({
        url: `user/${encodeURI(user_id)}/role/${encodeURI(role_id)}`,
        method: "PUT",
      }),
    }),

    // Roles

    // get all roles
    getRoles: builder.query({
      query: ({ page = 0, pageSize = 10 }) => ({
        url: `role${page !== null ? `?page_no=${page}` : ""}${pageSize ? `&page_size=${pageSize}` : ""
          }`,
        method: "GET",
      }),
    }),

    // User Preferences

    // GET ALL User Preference
    getUserPreferences: builder.query({
      query: ({ user_id = "", key = "", sort_by = "", sort_order = "" }) => ({
        url: `user/preference${user_id ? `?user_id=${user_id}` : ""}${key ? `&key=${key}` : ""
          }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_order ? `&sort_order=${sort_order}` : ""
          }`,
        method: "GET",
      }),
    }),

    // Create User Preference
    createUserPreference: builder.mutation({
      query: (body) => ({
        url: `user/preference`,
        method: "POST",
        body,
      }),
    }),

    // Delete User Preference
    deleteUserPreference: builder.mutation({
      query: (key) => ({
        url: `user/preference?key=${key}`,
        method: "DELETE",
      }),
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetUserByIdQuery,
  useLazyGetUsersQuery,
  useGetUsersQuery,
  useLazyGetUserByIdQuery,
  useLazyGetUsersAsOptionsQuery,
  useLazyGetRolesQuery,
  useGetUsersAsOptionsQuery,
  useUpdateRoleForUserMutation,
  useLazyGetUserPreferencesQuery,
  useCreateUserPreferenceMutation,
  useDeleteUserPreferenceMutation
} = usersAPI;
